/**
 * MKS-314 follow-up — the ONE place in the SPA that decides which currency a
 * money figure is shown in.
 *
 * Shaped exactly like `useDateFormatSync`, and for the same reason: money is
 * rendered from plain functions (`formatCurrency`, table cell renderers, chart
 * tick formatters) that cannot call a hook, so the rate set is resolved once
 * here at the top of `<App>` and written into the module-level value in
 * `utils/reportingMoney` that all of them read.
 *
 * The write happens during render, not in an effect, on purpose: `<App>` is an
 * ancestor of every money-rendering component, so they all render later in the
 * same pass and all see the same rates. An effect would land one render too
 * late and paint the first resolved frame in CZK. The write is idempotent and
 * derived only from the flag and the response, so re-running it is a no-op.
 *
 * Precedence, and why:
 *   1. MKS-314 flag ON, the rates loaded, and `converted` true → the instance
 *      reporting currency, at the per-month rates the backend resolved.
 *   2. Otherwise → `null`, which hands every figure back to plain CZK with no
 *      tooltip. This is the flag's OFF branch and it is byte-identical to what
 *      production renders today.
 *
 * The flag fails CLOSED. While the query is loading, for an unauthenticated
 * visitor, on error, and for a CZK instance reporting in CZK (`converted`
 * false), the app renders today's output rather than flickering through a
 * second currency.
 */
import { useQuery } from '@tanstack/react-query';
import { useFlag } from '../services/flagsContext';
import { fetchReportingRates, type ReportingRates } from '../services/api/reportingRates';
import {
  getActiveReportingFx,
  setActiveReportingFx,
  type ReportingFx,
} from '../utils/reportingMoney';

export const REPORTING_RATES_QUERY_KEY = ['reporting-rates'] as const;

/**
 * `staleTime: Infinity`, matching `useInstanceSettings`: a closed month's
 * average never changes and the open month's fixing changes once a day, so
 * re-reading on every window focus would re-render every money cell in the app
 * for a number that did not move.
 */
export function useReportingRates(enabled: boolean) {
  return useQuery<ReportingRates>({
    queryKey: REPORTING_RATES_QUERY_KEY,
    queryFn: fetchReportingRates,
    enabled,
    staleTime: Infinity,
  });
}

/**
 * The rate set the response describes, or `null` when nothing converts.
 *
 * `source` is checked as well as `converted` because the two must agree: a
 * `converted` response with no source would render figures in the reporting
 * currency while the tooltip could name no origin for them, which is exactly
 * the false attribution the backend's nullable `source` exists to prevent.
 */
export function toReportingFx(rates: ReportingRates | undefined): ReportingFx | null {
  if (!rates || !rates.converted || !rates.source) return null;
  if (rates.reportingCurrency === rates.ledgerCurrency) return null;
  return {
    currency: rates.reportingCurrency,
    ledgerCurrency: rates.ledgerCurrency,
    source: rates.source,
    months: rates.months,
    unavailableMonths: rates.unavailableMonths,
  };
}

/**
 * The response the installed rate set was built from.
 *
 * Module-level rather than a ref, for the same reason `useDateFormatSync`
 * keeps `lastLocale` there: the value it guards is itself module-level, and a
 * ref would let a second `<App>` mount in a test disagree with the rate set
 * actually installed.
 *
 * It exists so the installed object has a STABLE IDENTITY. `toReportingFx`
 * builds a new object on every call, and a surface that converts a whole
 * series inside a `useMemo` (the revenue chart) depends on that object: a
 * fresh identity each render would rebuild the memo every render, and no
 * dependency at all would leave it holding the CZK series it built before the
 * rates arrived.
 */
let lastRates: ReportingRates | undefined;
let lastEnabled = false;

/**
 * A render-STABLE handle on the installed rate set, for a `useMemo`
 * dependency list. Not a hook — it is a plain read of the value
 * {@link useReportingFxSync} installed earlier in the same render pass.
 *
 * The VALUE is deliberately opaque: the arithmetic belongs in
 * `utils/reportingMoney`, and a caller that read rates out of here would be a
 * second implementation of it.
 */
export function useReportingFxDep(): unknown {
  return getActiveReportingFx();
}

export function useReportingFxSync(): void {
  const enabled = useFlag('mks_314_company_currency_locale');

  // Gated on the flag, not only on auth: with the flag off this ticket must
  // cost production zero extra requests.
  const { data } = useReportingRates(enabled);

  // Only install when the inputs actually changed, so the installed object
  // keeps its identity between renders.
  if (data !== lastRates || enabled !== lastEnabled) {
    lastRates = data;
    lastEnabled = enabled;
    setActiveReportingFx(enabled ? toReportingFx(data) : null);
  }
}

/** Test seam: forget the installed response so the next sync re-installs it. */
export function resetReportingFxSyncForTests(): void {
  lastRates = undefined;
  lastEnabled = false;
}
